import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/ui/Navbar";
import { Card, CardContent } from "../components/ui/card";
import axios from "axios";
import { toast } from "react-toastify";
import {
  leerServicioActivoLocal,
  guardarServicioActivoLocal,
  verificarServicioActivoRemoto,
} from "../utils/servicioActivo";

// 🧩 Formularios específicos
import FormPreventivo from "../components/forms/FormPreventivo";
import FormCorrectivo from "../components/forms/FormCorrectivo";
import FormCalibracion from "../components/forms/FormCalibracion";
import ChatPanel from "../components/ServicioChat/ChatPanel";
import {
  LoaderPinwheel,
  ArrowLeft,
  Play,
  Clock,
  FileCheck2,
  PencilLine,
  Building2,
  Cpu,
  Tag,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  Info,
  Undo2,
} from "lucide-react";

/* ─── Helpers visuales ────────────────────────────────────────────── */
const EstadoBadge = ({ estado }) => {
  const map = {
    Pendiente: "bg-yellow-100 text-yellow-800 border-yellow-200",
    Borrador: "bg-violet-100 text-violet-800 border-violet-200",
    Completado: "bg-green-100 text-green-800 border-green-200",
    Cancelado: "bg-red-100 text-red-800 border-red-200",
    "En proceso": "bg-blue-100 text-blue-800 border-blue-200",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${map[estado] || "bg-gray-100 text-gray-700 border-gray-200"}`}
    >
      {estado}
    </span>
  );
};

const TipoBadge = ({ tipo }) => {
  const map = {
    Preventivo: "bg-blue-100 text-blue-800",
    Correctivo: "bg-orange-100 text-orange-800",
    "Calibración": "bg-purple-100 text-purple-800",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${map[tipo] || "bg-gray-100 text-gray-700"}`}
    >
      {tipo}
    </span>
  );
};

const InfoItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
    <Icon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
    <div className="min-w-0">
      <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-widest">
        {label}
      </p>
      <p className="text-sm text-gray-800 font-medium mt-0.5 break-words">
        {value || "—"}
      </p>
    </div>
  </div>
);

const formatTimer = (secs) => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0)
    return `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};
/* ──────────────────────────────────────────────────────────────────── */

const GestionServicioPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const API = process.env.REACT_APP_API_URL;
  const [gestionExistente, setGestionExistente] = useState(false);
  const [gestionData, setGestionData] = useState(null);
  const [verificandoGestion, setVerificandoGestion] = useState(true);
  const [modoEdicion, setModoEdicion] = useState(false);
  // eslint-disable-next-line
  const [gestionCargada, setGestionCargada] = useState(false);

  const [servicio, setServicio] = useState(location.state?.servicio || null);
  const [loading, setLoading] = useState(!servicio);
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  // ⏱️ control servicio activo
  const [activo, setActivo] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const [abriendo, setAbriendo] = useState(false);

  useEffect(() => {
    const verificarGestion = async () => {
      if (!servicio?._id) return;

      try {
        const res = await axios.get(
          `${API}/gestion/cronograma/${servicio._id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (res.data && res.data.length > 0) {
          const gestion = res.data[0];
          setGestionExistente(true);
          setGestionData(gestion);
        }
      } catch (err) {
        toast.error("Error verificando gestión:", err);
      } finally {
        setVerificandoGestion(false);
      }
    };

    verificarGestion();
  }, [servicio, API, token]);

  // 🚀 Cargar servicio
  useEffect(() => {
    const fetchServicio = async () => {
      try {
        const res = await axios.get(`${API}/cronograma/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setServicio(res.data);
      } catch (err) {
        // Si era el servicio en ejecución, el servidor lo libera si ya no es válido
        const estado =
          leerServicioActivoLocal()?.cronogramaId === id
            ? await verificarServicioActivoRemoto().catch(() => null)
            : null;
        if (estado && !estado.activo) {
          toast.info(
            "Este servicio ya no está disponible y fue cerrado. Ya puedes iniciar uno nuevo.",
          );
        } else {
          toast.error(err.response?.data?.message || "Error al cargar el servicio");
        }
      } finally {
        setLoading(false);
      }
    };

    if (!servicio) fetchServicio();
  }, [id, servicio, API, token]);

  // 🔎 Verificar si este servicio ya está activo (local o desde otro dispositivo)
  useEffect(() => {
    if (verificandoGestion) return;
    let cancelado = false;

    const data = leerServicioActivoLocal();
    const userStored = JSON.parse(localStorage.getItem("user") || "{}");
    const serverActivo = userStored?.servicioActivo?.toString();

    if (data?.cronogramaId === id) {
      setActivo(true);
      const diff = Math.floor((Date.now() - data.inicio) / 1000);
      setSegundos((data.acumuladoSegundos || 0) + diff);
    } else if (serverActivo === id) {
      // Fallback: el servidor marcó este servicio como activo (otro dispositivo lo inició)
      const inicio = userStored.servicioInicio
        ? new Date(userStored.servicioInicio).getTime()
        : Date.now();
      const acumuladoSegundos = gestionData?.tiempoInvertidoMinutos
        ? Math.round(gestionData.tiempoInvertidoMinutos * 60)
        : 0;

      localStorage.setItem(
        "servicioActivo",
        JSON.stringify({ cronogramaId: id, inicio, acumuladoSegundos }),
      );

      setActivo(true);
      const diff = Math.floor((Date.now() - inicio) / 1000);
      setSegundos(acumuladoSegundos + diff);
    } else {
      return;
    }

    // Confirmar con el servidor: pudo ser finalizado, eliminado o liberado
    // por un administrador desde otra sesión. Sin conexión se conserva lo local.
    verificarServicioActivoRemoto()
      .then((estado) => {
        if (cancelado) return;
        if (!estado.activo || estado.servicioActivo !== id) {
          setActivo(false);
          setSegundos(0);
          toast.info(
            estado.motivo
              ? `Este servicio ya no está en ejecución: ${estado.motivo}`
              : "Este servicio ya no está en ejecución",
          );
        }
      })
      .catch(() => {});

    return () => {
      cancelado = true;
    };
  }, [id, verificandoGestion, gestionData]);

  // ⏱️ Contador
  useEffect(() => {
    if (!activo) return;

    const interval = setInterval(() => {
      const data = JSON.parse(localStorage.getItem("servicioActivo"));
      if (!data) return;

      const diff = Math.floor((Date.now() - data.inicio) / 1000);
      const acumulado = data.acumuladoSegundos || 0;
      setSegundos(acumulado + diff);
    }, 1000);

    return () => clearInterval(interval);
  }, [activo]);

  // ▶ Abrir servicio
  const abrirServicio = async (esRetoma = false) => {
    if (abriendo) return;
    setAbriendo(true);
    try {
      const res = await axios.post(
        `${API}/cronograma/abrir`,
        { cronogramaId: id },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const acumuladoSegundos = esRetoma
        ? Math.max(0, Math.round((gestionData?.tiempoInvertidoMinutos || 0) * 60))
        : 0;

      // Usar la hora de inicio del servidor (en reintentos/recargas conserva la original)
      guardarServicioActivoLocal({
        cronogramaId: id,
        servicioInicio: res.data?.servicioInicio,
        acumuladoSegundos,
      });

      const inicio = res.data?.servicioInicio
        ? new Date(res.data.servicioInicio).getTime()
        : Date.now();
      setActivo(true);
      setSegundos(acumuladoSegundos + Math.max(0, Math.floor((Date.now() - inicio) / 1000)));
      toast.success(esRetoma ? "Calibración retomada" : "Servicio iniciado");
    } catch (err) {
      toast.error(
        err.response?.data?.message || "No se pudo iniciar el servicio",
      );
    } finally {
      setAbriendo(false);
    }
  };

  const handleDraftSaved = (gestionActualizada) => {
    setGestionExistente(true);
    if (gestionActualizada) setGestionData(gestionActualizada);
    setActivo(false);
    setSegundos(0);
  };

  const handleFinalized = (gestionActualizada) => {
    if (gestionActualizada) setGestionData(gestionActualizada);
    setActivo(false);
    setSegundos(0);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
        <div className="bg-white rounded-2xl p-10 shadow-sm flex flex-col items-center gap-3">
          <LoaderPinwheel className="w-10 h-10 animate-spin text-blue-500" />
          <p className="text-sm text-gray-500">Cargando servicio...</p>
        </div>
      </div>
    );
  }

  if (!servicio) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
          <p className="text-gray-500 mb-4">Servicio no encontrado.</p>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 mx-auto text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            <ArrowLeft className="w-4 h-4" /> Volver
          </button>
        </div>
      </div>
    );
  }

  const tipo = servicio.tipo;
  const realizadoPorId =
    gestionData?.realizadoPor?._id || gestionData?.realizadoPor || null;
  const puedeEditarGestion =
    servicio.estado === "Completado" &&
    gestionExistente &&
    gestionData?.estado === "Gestionado" &&
    !gestionData?.firmaAutorizacion &&
    realizadoPorId?.toString() === user?.id?.toString();
  // Calibración en borrador: solo la retoma el responsable asignado
  const responsableId =
    servicio.personaAsignada?._id || servicio.personaAsignada || null;
  const esResponsable =
    !!responsableId && responsableId.toString() === user?.id?.toString();
  // Segundo firmante: revisa la calibración finalizada en solo lectura
  const firmanteId =
    servicio.segundoFirmante?.usuario?._id || servicio.segundoFirmante?.usuario || null;
  const enRevision =
    tipo === "Calibración" &&
    !activo &&
    !!firmanteId &&
    firmanteId.toString() === user?.id?.toString() &&
    gestionData?.estado === "Finalizado";
  const devolucion =
    tipo === "Calibración" && servicio.estado === "Borrador" && esResponsable
      ? servicio.segundoFirmante?.ultimaDevolucion
      : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-6 pt-20 md:pt-28 space-y-4">

        {/* ── Card principal: header + info equipo ── */}
        <Card className="shadow-sm border-0 rounded-2xl overflow-hidden">
          {/* Header con gradiente */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-3">
                <div className="bg-white/15 rounded-xl p-2">
                  <ClipboardList className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white leading-tight">
                    Gestión del servicio
                  </h2>
                  <div className="mt-1.5">
                    <TipoBadge tipo={tipo} />
                  </div>
                </div>
              </div>
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-1.5 text-white/75 hover:text-white text-sm font-medium transition-colors mt-0.5"
              >
                <ArrowLeft className="w-4 h-4" />
                Volver
              </button>
            </div>
          </div>

          <CardContent className="p-5 sm:p-6 space-y-5">

            {/* Verificando */}
            {verificandoGestion && (
              <div className="flex items-center gap-2.5 text-sm text-gray-500 bg-gray-50 rounded-xl px-4 py-3">
                <LoaderPinwheel className="w-4 h-4 animate-spin flex-shrink-0" />
                Verificando estado del servicio…
              </div>
            )}

            {/* Abrir servicio */}
            {!verificandoGestion && !activo && !gestionExistente && (
              <button
                onClick={() => abrirServicio(false)}
                disabled={abriendo}
                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors shadow-sm text-sm"
              >
                <Play className="w-4 h-4 fill-white" />
                {abriendo ? "Abriendo…" : "Abrir servicio"}
              </button>
            )}
            {!verificandoGestion &&
              !activo &&
              gestionExistente &&
              tipo === "Calibración" &&
              gestionData?.estado === "Gestionado" &&
              esResponsable && (
                <button
                  onClick={() => abrirServicio(true)}
                  disabled={abriendo}
                  className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors shadow-sm text-sm"
                >
                  <Play className="w-4 h-4 fill-white" />
                  Retomar calibración
                </button>
              )}

            {/* Timer */}
            {activo && !gestionExistente && (
              <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                <div className="bg-emerald-500 rounded-full p-1.5 flex-shrink-0">
                  <Clock className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-[10px] text-emerald-600 font-semibold uppercase tracking-widest">
                    En ejecución
                  </p>
                  <p className="text-xl font-bold text-emerald-700 font-mono leading-tight">
                    {formatTimer(segundos)}
                  </p>
                </div>
              </div>
            )}

            {/* Gestión existente */}
            {gestionExistente && (
              <div className="space-y-3">
                <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                  <FileCheck2 className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  <p className="text-sm text-blue-700 font-medium">
                    Este servicio ya tiene una gestión registrada
                  </p>
                </div>
                {puedeEditarGestion && !modoEdicion && (
                  <button
                    onClick={() => setModoEdicion(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl text-sm transition-colors"
                  >
                    <PencilLine className="w-4 h-4" />
                    Editar servicio
                  </button>
                )}
                {modoEdicion && (
                  <div className="flex items-center gap-2.5 text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm font-medium">
                    <PencilLine className="w-4 h-4 flex-shrink-0" />
                    Edición habilitada. Realiza los cambios y guarda.
                  </div>
                )}
              </div>
            )}

            {enRevision && (
              <div className="flex items-start gap-3 bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
                <ClipboardCheck className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-violet-800">
                  Revisión en solo lectura. Firma o devuelve esta calibración desde
                  Mis servicios, con la opción Revisar y firmar.
                </p>
              </div>
            )}
            {devolucion?.motivo && (
              <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                <Undo2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-800">
                  <p className="font-semibold">Devuelta por el segundo firmante</p>
                  <p className="mt-0.5 whitespace-pre-line">{devolucion.motivo}</p>
                </div>
              </div>
            )}

            {/* Info del equipo */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Cpu className="w-4 h-4 text-gray-400" />
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-widest">
                  Información del equipo
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <InfoItem
                  icon={Building2}
                  label="Cliente"
                  value={`${servicio.equipo?.cliente?.name || "—"} (${servicio.equipo?.cliente?.nit || "—"})`}
                />
                <InfoItem
                  icon={Cpu}
                  label="Equipo"
                  value={`[${servicio.equipo?.categoria?.name}] ${servicio.equipo?.marca?.name} - ${servicio.equipo?.modelo?.name} [${servicio.equipo?.serial || "—"}]`}
                />
                <InfoItem
                  icon={Tag}
                  label="Tipo de servicio"
                  value={tipo}
                />
                <InfoItem
                  icon={CalendarDays}
                  label="Fecha programada"
                  value={new Date(servicio.fechaProgramada).toLocaleDateString("es-CO")}
                />
                {/* Observaciones + estado en fila completa */}
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl sm:col-span-2">
                  <Info className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-widest">
                      Observaciones
                    </p>
                    <p className="text-sm text-gray-800 mt-0.5">
                      {servicio.observaciones || "No registra"}
                    </p>
                  </div>
                  <EstadoBadge estado={servicio.estado} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Chat RAG (solo PRO/BLACK) ── */}
        {(() => {
          const planActual = (localStorage.getItem("planActual") || "FREE").toUpperCase();
          const canAccessRagChat = ["PRO", "BLACK"].includes(planActual);
          return canAccessRagChat && servicio.equipo?._id ? (
            <ChatPanel equipoId={servicio.equipo._id} />
          ) : null;
        })()}

        {/* ── Formulario dinámico ── */}
        {!activo && !gestionExistente && (
          <Card className="shadow-sm border-0 rounded-2xl">
            <CardContent className="p-6">
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="bg-gray-100 rounded-full p-4 mb-4">
                  <Play className="w-8 h-8 text-gray-300" />
                </div>
                <p className="text-gray-600 font-semibold">Servicio no iniciado</p>
                <p className="text-sm text-gray-400 mt-1">
                  Para registrar información debes{" "}
                  <strong className="text-gray-500">abrir el servicio</strong>.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {(activo || enRevision) && (
          <Card className="shadow-sm border-0 rounded-2xl">
            <CardContent className="p-5 sm:p-6">
              {tipo === "Preventivo" && (
                <FormPreventivo
                  servicio={servicio}
                  gestionData={gestionData}
                  canEdit={servicio.estado === "Pendiente" || modoEdicion}
                />
              )}
              {tipo === "Correctivo" && (
                <FormCorrectivo
                  servicio={servicio}
                  gestionData={gestionData}
                  canEdit={servicio.estado === "Pendiente" || modoEdicion}
                />
              )}
              {tipo === "Calibración" && (
                <FormCalibracion
                  servicio={servicio}
                  gestionData={gestionData}
                  canEdit={servicio.estado === "Pendiente" || modoEdicion}
                  tiempoSegundos={segundos}
                  soloRevision={enRevision}
                  onDraftSaved={handleDraftSaved}
                  onFinalized={handleFinalized}
                />
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default GestionServicioPage;
